import { Trans, useLingui } from "@lingui/react/macro";
import {
  patientKeys,
  removeRelative,
  useParentRoutesPatient,
} from "@/api/Patients";
import { PlainButton } from "@medoma/components/Button/Button";
import { AddRelative } from "@/forms/AddRelative";
import { EditRelative } from "@/forms/EditRelative";
import { EditAddress } from "@/forms/EditAddress";
import { EditAdditionalAddressInformation } from "@/forms/EditAdditionalAddressInformation";
import { EditKeyToPatient } from "@/forms/EditKeyToPatient";
import { EditInformation } from "@/forms/EditInformation";
import { EditPhoneNumber } from "@/forms/EditPhoneNumber";
import { EditPrimaryResponsible } from "@/forms/EditPrimaryResponsible";
import {
  deducedError,
  displayErrorMessageAlert,
  isErrorWithKnownErrorCode,
  knownErrorCodeSchema,
} from "@/Utils/ErrorUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import styles from "./EditInformationWrappers.module.scss";
import { Heading } from "@medoma/components/Heading/Heading";
import { EditSafetyAlarmText } from "@/forms/EditSafetyAlarm";
import { EditPets } from "@/forms/EditPets";
import { EditHealthcareJourneyNumber } from "@/forms/EditHealthcareJourneyNumber";
import { EditHomeCare } from "@/forms/EditHomeCare";
import { EditLifeCare } from "@/forms/EditLifeCare";
import { EditDiagnosisCode } from "@/forms/EditDiagnosisCode";
import { EditExternalCareEpisodeId } from "@/forms/EditExternalCareEpisodeId";
import { EditPlannedAdmissionDateTime } from "@/forms/EditPlannedAdmissionDateTime";
import { EditPlannedDischargeDateTime } from "@/forms/EditPlannedDischargeDateTime";
import { EditFlags } from "@/forms/EditFlags";
import { EditArea } from "@/forms/EditArea";
import { EditReferringUnit } from "@/forms/EditReferringUnit";
import { EditPlaceOfAssessment } from "@/forms/EditPlaceOfAssessment";
import { EditCareSummary } from "@/forms/EditCareSummary";
import { EditPaymentExempt } from "@/forms/EditPaymentExempt";
import { EditLanguage } from "@/forms/EditLanguage";
import { EditReasonForAdmission } from "@/forms/EditReasonForAdmission";
import { localizePhoneNumber } from "@medoma/models/phoneNumbers";
import { useUserProfile } from "@/api/Users";
import { getPatientFullName } from "@medoma/models/patients";
import { useState } from "react";

export const EditDiagnosisCodeWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra diagnoskoden</Trans>
      </Heading>
      <EditDiagnosisCode
        patientId={patient.id}
        currentDiagnosisCode={patient.diagnosis?.code}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditExternalCareEpisodeIdWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra externt vårdperiodnummer</Trans>
      </Heading>
      <EditExternalCareEpisodeId
        patientId={patient.id}
        currentExternalCareEpisodeId={patient.externalCareEpisodeId}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditPlannedAdmissionDateTimeWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra planerad inskrivning</Trans>
      </Heading>
      <EditPlannedAdmissionDateTime
        patientId={patient.id}
        currentPlannedAdmissionDateTime={patient.plannedAdmissionDateTime}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditPlannedDischargeDateTimeWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra planerad utskrivning</Trans>
      </Heading>
      <EditPlannedDischargeDateTime
        patientId={patient.id}
        currentPlannedDischargeDateTime={patient.plannedDischargeDateTime}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditAddressWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra adressen</Trans>
      </Heading>
      <EditAddress
        patientId={patient.id}
        currentAddress={patient.address}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditAdditionalAddressInformationWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra övrig adressinformation</Trans>
      </Heading>
      <EditAdditionalAddressInformation
        patientId={patient.id}
        currentAddress={patient.address}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditKeyToPatientWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra information om nyckel</Trans>
      </Heading>
      <EditKeyToPatient
        patientId={patient.id}
        currentAddress={patient.address}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditPhoneNumberWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra telefonnumret</Trans>
      </Heading>
      <EditPhoneNumber
        patientId={patient.id}
        currentPhoneNumber={patient.phoneNumber}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditRelativesWrapper = () => {
  const { t } = useLingui();
  const { countryCode } = useUserProfile();
  const patient = useParentRoutesPatient();
  const patientName = getPatientFullName(patient.name);
  const navigate = useNavigate();
  const [editingRelativeId, setEditingRelativeId] = useState<string | null>(
    null,
  );

  const queryClient = useQueryClient();
  const { mutate: removeRelativeMutation, isPending: isRemovingRelative } =
    useMutation({
      mutationFn: ({
        relativeId,
        patientId,
      }: {
        relativeId: string;
        patientId: string;
      }) => removeRelative(patientId, relativeId),
      onError: (error, { patientId }) => {
        if (
          isErrorWithKnownErrorCode(error) &&
          error.data.code === knownErrorCodeSchema.enum.RelativeMissing
        ) {
          return queryClient.invalidateQueries({
            queryKey: patientKeys.detail(patientId),
          });
        } else {
          displayErrorMessageAlert(
            `${t`Det gick inte att ta bort anhörig.`} ${deducedError(error)}`,
          );
        }
      },
      onSuccess: (_, { patientId }) => {
        return queryClient.invalidateQueries({
          queryKey: patientKeys.detail(patientId),
        });
      },
    });

  const editingRelative = patient.relatives.find(
    (relative) => relative.id === editingRelativeId,
  );

  return (
    <div className={styles.relatives}>
      {patient.relatives.length > 0 ? (
        <section className={styles.editFormContainer}>
          <Heading level="h2">
            <Trans>Ändra anhöriga</Trans>
          </Heading>
          <ul className={styles.list}>
            {patient?.relatives.map((relative) => {
              const { id, name, relation, isLivingTogether, phoneNumber } =
                relative;
              const optionalLivesTogetherWithSegment = isLivingTogether
                ? t`bor med ${patientName} och`
                : "";
              const localizedPhoneNumber = phoneNumber
                ? localizePhoneNumber({
                    countryCode,
                    phoneNumber,
                  })
                : null;

              if (editingRelativeId === id && editingRelative) {
                return (
                  <li key={id}>
                    <EditRelative
                      patientId={patient.id}
                      currentRelative={editingRelative}
                      onSuccess={() => {
                        setEditingRelativeId(null);
                        void navigate("..");
                      }}
                      onCancel={() => setEditingRelativeId(null)}
                    />
                  </li>
                );
              }

              return (
                <li key={id}>
                  <p>
                    {localizedPhoneNumber ? (
                      <Trans>
                        {name} ({relation}) {optionalLivesTogetherWithSegment}{" "}
                        nås på {localizedPhoneNumber}
                      </Trans>
                    ) : (
                      <Trans>
                        {name} ({relation}) {optionalLivesTogetherWithSegment}{" "}
                        saknar telefonnummer
                      </Trans>
                    )}
                  </p>
                  <div className={styles.buttonGroup}>
                    <PlainButton
                      onClick={() => setEditingRelativeId(id)}
                      disabled={isRemovingRelative}
                    >
                      <Trans>Ändra</Trans>
                    </PlainButton>
                    <PlainButton
                      onClick={() =>
                        removeRelativeMutation({
                          patientId: patient.id,
                          relativeId: id,
                        })
                      }
                      disabled={isRemovingRelative}
                    >
                      <Trans>Ta bort</Trans>
                    </PlainButton>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ) : undefined}
      <section className={styles.editFormContainer}>
        <Heading level="h2">
          <Trans>Lägg till anhörig</Trans>
        </Heading>
        <AddRelative patientId={patient.id} onSuccess={() => navigate("..")} />
      </section>
    </div>
  );
};

export const EditPrimaryResponsibleWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra patientansvarig</Trans>
      </Heading>
      <EditPrimaryResponsible
        patientId={patient.id}
        currentPrimaryResponsible={patient.primaryResponsible}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditOtherInformationWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra allmän information</Trans>
      </Heading>
      <EditInformation
        patientId={patient.id}
        currentInformation={patient.information}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditSafetyAlarmWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra sociala insatser</Trans>
      </Heading>
      <EditSafetyAlarmText
        patientId={patient.id}
        currentSafetyAlarmText={patient.safetyAlarmText}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditPetsWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra husdjur</Trans>
      </Heading>
      <EditPets
        patientId={patient.id}
        currentPets={patient.pet}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditHealthcareJourneyNumberWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra sjukresenummer</Trans>
      </Heading>
      <EditHealthcareJourneyNumber
        patientId={patient.id}
        currentHealthcareJourneyNumber={patient.healthcareJourneyNumber}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditHomeCareWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra hemtjänst</Trans>
      </Heading>
      <EditHomeCare
        patientId={patient.id}
        currentHomeCare={patient.homeCare}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditLifeCareWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra LifeCare</Trans>
      </Heading>
      <EditLifeCare
        patientId={patient.id}
        currentLifeCare={patient.lifeCare}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditFlagsWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra flaggningar</Trans>
      </Heading>
      <EditFlags
        patientId={patient.id}
        currentFlags={patient.flags}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditCareSummaryWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra vårdsammanfattning</Trans>
      </Heading>
      <EditCareSummary
        patientId={patient.id}
        currentCareSummary={patient.careSummary}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditReferringUnitWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra remitterande enhet</Trans>
      </Heading>
      <EditReferringUnit
        patientId={patient.id}
        currentReferringUnit={patient.referringUnit}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditAreaWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra område</Trans>
      </Heading>
      <EditArea
        patientId={patient.id}
        siteId={patient.site.id}
        currentArea={patient.area}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditPlaceOfAssessmentWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Uppdaterar plats för bedömning</Trans>
      </Heading>
      <EditPlaceOfAssessment
        patientId={patient.id}
        currentPlaceOfAssessment={patient.placeOfAssessment}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditPaymentExemptWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra betalningsbefriad</Trans>
      </Heading>
      <EditPaymentExempt
        patientId={patient.id}
        currentPaymentExempt={patient.paymentExempt}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditLanguageWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra språk</Trans>
      </Heading>
      <EditLanguage
        patientId={patient.id}
        currentLanguage={patient.language}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};

export const EditReasonForAdmissionWrapper = () => {
  const patient = useParentRoutesPatient();
  const navigate = useNavigate();

  return (
    <section className={styles.editFormContainer}>
      <Heading level="h2">
        <Trans>Ändra inskrivningsorsak</Trans>
      </Heading>
      <EditReasonForAdmission
        patientId={patient.id}
        currentReasonForAdmission={patient.reasonForAdmission}
        onSuccess={() => navigate("..")}
      />
    </section>
  );
};
